import { cn } from '@workspace/ui/lib/utils'
import type { VariantProps } from 'class-variance-authority'
import { ArrowRight, Loader2 } from 'lucide-react'
import type * as React from 'react'
import { dsmButtonBase } from './button-base.js'

/**
 * The primary call to action — one per screen.
 *
 * Solid orange, so it is the loudest thing on the page. It has no colour
 * variants on purpose: if an action needs to be quieter it is a
 * `SecondaryButton`, and destructive actions are that component's `danger`
 * tone. `className` is for layout (margin, grid placement); the look is not
 * overridable.
 */
const CTA_TONE = 'bg-primary text-primary-foreground hover:bg-primary-hover'

type BaseVariants = VariantProps<typeof dsmButtonBase>

export interface CtaButtonProps extends React.ComponentProps<'button'>, BaseVariants {
  /** Icon shown before the label. */
  icon?: React.ReactNode
  /** Trailing arrow, for CTAs that move the user somewhere. */
  arrow?: boolean
  /** Swaps the leading icon for a spinner and disables the button. */
  loading?: boolean
}

export function CtaButton({
  className,
  size,
  fullWidth,
  icon,
  arrow,
  loading,
  disabled,
  type = 'button',
  children,
  ...props
}: CtaButtonProps) {
  return (
    <button
      data-slot="cta-button"
      type={type}
      className={cn(dsmButtonBase({ size, fullWidth }), CTA_TONE, className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" /> : icon}
      <span>{children}</span>
      {arrow && <ArrowRight />}
    </button>
  )
}

/**
 * Classes for a CTA that has to be an anchor or a router `Link` rather than a
 * button. Compose your own children — the label still belongs in a `<span>`.
 */
export function ctaLinkClasses({ size, fullWidth }: BaseVariants = {}) {
  return cn(dsmButtonBase({ size, fullWidth }), CTA_TONE)
}
